import type { HouseholdItem } from './types';

/** Gap fillers: events + demo obligations when APIs are empty or sparse. */
export function buildMockHousehold(reference = new Date()): HouseholdItem[] {
  const y = reference.getFullYear();
  const m = reference.getMonth();
  const iso = (day: number, hour = 9) =>
    new Date(y, m, day, hour, 0, 0, 0).toISOString();

  return [
    {
      id: 'mock-car-insurance',
      title: 'Car insurance',
      category: 'renewal',
      status: 'needs_attention',
      dueAt: iso(28),
      amount: 1120,
      currency: 'AUD',
      recurrence: 'annual',
      source: 'bank_detected',
      paperKind: 'car',
      detection: {
        summary: "Based on last year's payment.",
        lastPaidAt: new Date(y - 1, m, 28).toISOString(),
        lastPaidAmount: 1084,
        expectedRecurrence: 'annual',
        foundIn: 'Found in Commonwealth Bank transactions',
        confidence: 0.94,
        anomaly: {
          kind: 'amount_increase',
          detail: 'Expected amount is slightly higher than last year ($1,084 → $1,120).',
        },
      },
    },
    {
      id: 'mock-health-insurance',
      title: 'Health insurance',
      category: 'renewal',
      status: 'upcoming',
      dueAt: iso(14),
      amount: 214,
      currency: 'AUD',
      recurrence: 'monthly',
      source: 'recurring',
      paperKind: 'house',
      detection: {
        summary: 'Same insurance payment every month.',
        lastPaidAt: new Date(y, m - 1, 14).toISOString(),
        lastPaidAmount: 214,
        expectedRecurrence: 'monthly',
        foundIn: 'Recurring on the 14th',
        confidence: 0.96,
      },
    },
    {
      id: 'mock-electricity',
      title: 'Electricity',
      category: 'bill',
      status: 'upcoming',
      dueAt: iso(9),
      amount: 198,
      currency: 'AUD',
      recurrence: 'quarterly',
      source: 'bank_detected',
      paperKind: 'envelope',
      detection: {
        summary: 'Utility bill pattern detected from statements.',
        lastPaidAt: new Date(y, m - 3, 8).toISOString(),
        lastPaidAmount: 186,
        expectedRecurrence: 'quarterly',
        foundIn: 'Found in utility payment history',
        confidence: 0.87,
      },
    },
    {
      id: 'mock-internet',
      title: 'Internet',
      category: 'bill',
      status: 'upcoming',
      dueAt: iso(3),
      amount: 89,
      currency: 'AUD',
      recurrence: 'monthly',
      source: 'recurring',
      paperKind: 'envelope',
      detection: {
        summary: 'Recurring subscription payment.',
        expectedRecurrence: 'monthly',
        confidence: 0.9,
      },
    },
    {
      id: 'mock-school-fees',
      title: 'School fees',
      category: 'bill',
      status: 'needs_attention',
      dueAt: iso(5),
      amount: 1200,
      currency: 'AUD',
      recurrence: 'quarterly',
      source: 'bank_detected',
      paperKind: 'document',
      detection: {
        summary: 'School fees that recur each term.',
        expectedRecurrence: 'quarterly',
        foundIn: 'Detected from previous term payments',
        confidence: 0.91,
      },
    },
    {
      id: 'mock-netflix',
      title: 'Netflix',
      category: 'renewal',
      status: 'upcoming',
      dueAt: iso(18),
      amount: 25.99,
      currency: 'AUD',
      recurrence: 'monthly',
      source: 'recurring',
      paperKind: 'document',
      detection: {
        summary: 'Subscription renews on a predictable date.',
        expectedRecurrence: 'monthly',
        confidence: 0.93,
      },
    },
    {
      id: 'mock-birthday',
      title: "Maya's Birthday",
      category: 'event',
      status: 'upcoming',
      dueAt: iso(22, 10),
      source: 'mock',
      paperKind: 'gift',
      detection: {
        summary: 'Family event from household calendar patterns.',
        confidence: 0.75,
      },
    },
    {
      id: 'mock-gift-reminder',
      title: 'Buy birthday gift',
      category: 'reminder',
      status: 'needs_attention',
      dueAt: iso(19),
      source: 'mock',
      paperKind: 'gift',
      notes: 'Prepare something thoughtful before the weekend.',
      detection: {
        summary: 'Reminder linked to an upcoming family event.',
        confidence: 0.8,
      },
    },
    {
      id: 'mock-dentist',
      title: 'Dentist Appointment',
      category: 'event',
      status: 'upcoming',
      dueAt: iso(11, 14),
      source: 'calendar',
      paperKind: 'calendar',
      detection: {
        summary: 'Appointment surfaced for this month.',
        confidence: 0.78,
      },
    },
    {
      id: 'mock-travel',
      title: 'Family Trip — Packing',
      category: 'reminder',
      status: 'upcoming',
      dueAt: iso(25),
      source: 'mock',
      paperKind: 'passport',
      detection: {
        summary: 'Travel preparation reminder.',
        confidence: 0.72,
      },
    },
    {
      id: 'mock-council',
      title: 'Council Rates',
      category: 'bill',
      status: 'upcoming',
      dueAt: iso(30),
      amount: 640,
      currency: 'AUD',
      recurrence: 'quarterly',
      source: 'bank_detected',
      paperKind: 'house',
      detection: {
        summary: 'Detected from previous council rate payments.',
        expectedRecurrence: 'quarterly',
        foundIn: 'Found in bank statements',
        confidence: 0.89,
      },
    },
    {
      id: 'mock-school-concert',
      title: 'School Concert',
      category: 'event',
      status: 'upcoming',
      dueAt: iso(16, 18),
      source: 'calendar',
      paperKind: 'calendar',
      detection: {
        summary: 'School event for this month.',
        confidence: 0.77,
      },
    },
  ];
}

export function expandMockAcrossYear(reference = new Date()): HouseholdItem[] {
  const base = buildMockHousehold(reference);
  const extras: HouseholdItem[] = [];
  for (let offset = -2; offset <= 9; offset++) {
    if (offset === 0) continue;
    const pivot = new Date(reference.getFullYear(), reference.getMonth() + offset, 15);
    const monthItems = buildMockHousehold(pivot).slice(0, 4).map((item, idx) => ({
      ...item,
      id: `${item.id}-y${offset}-${idx}`,
      dueAt: new Date(
        pivot.getFullYear(),
        pivot.getMonth(),
        Math.min(28, new Date(item.dueAt).getDate())
      ).toISOString(),
      status: 'upcoming' as const,
    }));
    extras.push(...monthItems);
  }
  return [...base, ...extras];
}
